import type { KeyboardEvent, ReactNode } from "react";
import classNames from "classnames";
import Tag, { type TagStyle, type TagType } from "../Tag/Tag";
import { LitCheckbox } from "./LitCheckbox";

export interface CheckboxCardProps {
  headline: ReactNode;
  supportingText?: string;
  onChange: (value: boolean) => void;
  checked: boolean;
  name: string;
  disabled?: boolean;
  testId?: string;
  rank?: number;
  tagText?: string;
  tagType?: TagType;
  tagStyle?: TagStyle;
}

export default function CheckboxCard({
  headline,
  supportingText,
  onChange,
  checked,
  disabled = false,
  name,
  testId,
  rank,
  tagText,
  tagType,
  tagStyle = "strong"
}: CheckboxCardProps) {
  const accessibleName =
    typeof headline === "string"
      ? `${rank != null ? `${rank}. ` : ""}${headline}${supportingText ? `. ${supportingText}` : ""}${tagText ? `. ${tagText}` : ""}`
      : undefined;

  const handleActivate = () => {
    if (disabled) return;
    onChange(!checked);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled || (event.key !== "Enter" && event.key !== " ")) return;
    event.preventDefault();
    handleActivate();
  };

  return (
    <div
      data-testid={testId}
      role="checkbox"
      aria-checked={checked}
      aria-disabled={disabled || undefined}
      aria-label={accessibleName}
      tabIndex={disabled ? -1 : 0}
      className={classNames(
        "rounded-xl border p-5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sys-brand-primary",
        {
          "border-sys-brand-primary bg-sys-brand-surface-container-lowest": checked,
          "border-sys-brand-outline-variant bg-sys-brand-surface": !checked,
          "cursor-pointer": !disabled,
          "cursor-not-allowed": disabled
        }
      )}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-start gap-4">
        <div className="pointer-events-none flex h-6 shrink-0 items-center" aria-hidden>
          <LitCheckbox
            className="shrink-0"
            disabled={disabled}
            checked={checked}
            name={name}
            id={name}
            tabIndex={-1}
            onChange={() => undefined}
          />
        </div>
        <div
          className={classNames("flex min-w-0 flex-1 flex-col gap-1", {
            "text-sys-brand-on-surface-variant opacity-[0.38]": disabled
          })}
        >
          <div className="flex min-h-6 items-center">
            {rank != null ? (
              <span
                className="mr-2 text-body-large-semibold text-sys-brand-primary"
                data-testid={testId ? `${testId}-rank` : undefined}
              >
                {rank}
              </span>
            ) : null}
            <span
              className={classNames(
                "min-w-0 flex-1 break-words text-sys-brand-on-surface",
                supportingText ? "text-body-large-semibold" : "text-body-large"
              )}
            >
              {headline}
            </span>
          </div>
          {supportingText ? (
            <span className="break-words text-body-large text-sys-brand-on-surface-variant">{supportingText}</span>
          ) : null}
          {tagText ? (
            <div className="flex">
              <Tag tagType={tagType} tagStyle={tagStyle} size="small" testId={testId ? `${testId}-tag` : undefined}>
                {tagText}
              </Tag>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
